import {OtsDateTime} from "../../components/OtsDateTime";
import {
  ActivityPeriod,
  ActivityPeriodSummary,
  ActivityPeriodSummarySource,
  ObjectChangeLog,
} from "@ots-frontend/ots-data";
import {ArrowPathIcon, CircleStackIcon, XMarkIcon} from "@heroicons/react/20/solid";
import {useMemo, useState} from "react";
import {AdminPanel} from "../../components/AdminPanel";
import {MetricStrip} from "../../components/MetricStrip";
import {DescriptionItem, DescriptionList} from "../../components/DescriptionList";
import {OtsActionButton, OtsAsyncActionButton} from "../../components/OtsButton";
import {
  OtsDataTable,
  OtsDataTableBody,
  OtsDataTableCell,
  OtsDataTableHead,
  OtsDataTableHeaderCell,
  OtsDataTableRow,
  OtsDataTableRowHeader,
  OtsDataTableSection,
  OtsKeyValueTable,
} from "../../components/OtsDataTable";
import {OtsTimeline} from "../../components/OtsTimeline";
import {StatusPill} from "../../components/StatusPill";
import {ConfirmationDialog} from "../../dialogs/ConfirmationDialog";
import {OtsBrand, OtsColorSchemePreference} from "../../theme/OtsTheme";
import {EntityComments} from "../EntityProfile/EntityComments";
import {EntityProfile} from "../EntityProfile/EntityProfile";
import {EntityChangeLog} from "../EntityProfile/EntityChangeLog";
import {ActivityPeriodHeaderBadges, getActivityPeriodStatusTone} from "./ActivityPeriodHeaderBadges";
import {ActivityPeriodReports, collectActivityPeriodReports} from "./ActivityPeriodReports";

export type ActivityPeriodProfileTab = "overview" | "reports" | "comments" | "change-log";

export type ActivityPeriodProfileProps = {
  activityPeriod: ActivityPeriod;
  displayName: string;
  summary?: ActivityPeriodSummary;
  summaryLoading?: boolean;
  summaryError?: unknown;
  summarySource?: ActivityPeriodSummarySource;
  changeLogs?: ObjectChangeLog[];
  changeLogsLoading?: boolean;
  tenantCode?: string;
  allowAddComment?: boolean;
  showSummaryActions?: boolean;
  refreshingSummary?: boolean;
  refreshError?: unknown;
  density?: "compact" | "default";
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  onSummarySourceChange?: (source: ActivityPeriodSummarySource) => void;
  onRefreshSummary?: () => void | Promise<void>;
  onClose?: () => void;
};

const number = new Intl.NumberFormat();
const decimal = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2});
const currency = new Intl.NumberFormat(undefined, {style: "currency", currency: "USD"});
const formatResponsibilityAction = (value: string) => value
  .replace(/_/g, " ")
  .toLowerCase()
  .replace(/(^|\s)\S/g, character => character.toUpperCase());

const SummaryContent = ({summary, loading, error, density}: {
  summary?: ActivityPeriodSummary;
  loading?: boolean;
  error?: unknown;
  density: "compact" | "default";
}) => {
  if (loading) return <p role="status" className="text-sm text-[var(--ots-text-muted)]">Loading activity period summary…</p>;
  if (error) return <p role="alert" className="text-sm text-[var(--ots-error)]">The summary could not be loaded.</p>;
  if (!summary) return <p className="text-sm text-[var(--ots-text-muted)]">No rollup is available yet.</p>;

  const totals = summary.totals;
  const totalCost = summary.rateModels.reduce((sum, rateModel) => sum + (rateModel.totalCost ?? 0), 0);
  const responsibilityRemaining = summary.personalResponsibilityCounts.reduce((sum, item) => sum + item.remainingCount, 0);

  return <div className={density === "compact" ? "space-y-4" : "space-y-6"}>
    <MetricStrip density={density} maxVisible={5} ariaLabel="Activity period summary" metrics={[
      {id: "tickets", label: "Tickets", value: number.format(totals.ticketCount ?? 0), tone: "primary", priority: 10, tooltip: "Tickets in this activity period"},
      {id: "quantity", label: "Quantity", value: decimal.format(totals.totalQty ?? 0), tone: "primary", priority: 20, tooltip: "Total transported quantity"},
      {id: "cost", label: "Cost", value: currency.format(totalCost), tone: "success", hidden: !summary.rateModels.some(model => model.totalCost != null), priority: 30, tooltip: "Total visible cost across rate models"},
      {id: "disputes", label: "Disputes", value: number.format(totals.disputeCount ?? 0), tone: (totals.disputeCount ?? 0) ? "warning" : "neutral", priority: 40, tooltip: "Tickets with active disputes"},
      {
        id: "responsibilities",
        label: "My responsibilities",
        value: number.format(responsibilityRemaining),
        tone: responsibilityRemaining ? "warning" : "neutral",
        priority: 5,
        tooltip: "Your remaining responsibilities for this activity period",
        popover: <div className={density === "compact" ? "min-w-56" : "min-w-64"}>
          <h3 className="font-semibold text-[var(--ots-secondary)]">My responsibilities</h3>
          {summary.personalResponsibilityCounts.length ? <OtsKeyValueTable
            className="mt-2"
            ariaLabel="Responsibility details"
            density={density}
            items={summary.personalResponsibilityCounts.map(item => ({
              id: item.action,
              label: formatResponsibilityAction(item.action),
              value: `${number.format(item.remainingCount)} remaining`,
            }))}/>
            : <p className="mt-2 text-[var(--ots-text-muted)]">No responsibilities are assigned to you.</p>}
        </div>,
      },
    ]}/>

    <DescriptionList className={density === "compact" ? "gap-y-4" : undefined}>
      <DescriptionItem label="Ticket coding">{number.format(totals.codingCount ?? 0)}</DescriptionItem>
      <DescriptionItem label="Coding disputes">{number.format(totals.codingDisputeCount ?? 0)}</DescriptionItem>
      <DescriptionItem label="Admin validations">{number.format(totals.adminValidationCount ?? 0)}</DescriptionItem>
      <DescriptionItem label="Field validations">{number.format(totals.fieldValidationCount ?? 0)}</DescriptionItem>
      <DescriptionItem label="Business validations">{number.format(totals.businessValidationCount ?? 0)}</DescriptionItem>
      <DescriptionItem label="Holder data">{totals.holderAvailable == null ? "Unknown" : totals.holderAvailable ? "Available" : "Not available"}</DescriptionItem>
      <DescriptionItem label="Last calculated"><OtsDateTime value={totals.refreshedDttm} empty="Not calculated" /></DescriptionItem>
      {totals.dirty && <DescriptionItem label="Rebuild pending since"><OtsDateTime value={totals.dirtyDttm} empty="Pending" /></DescriptionItem>}
    </DescriptionList>

    {!!summary.rateModels.length && <OtsDataTableSection title="Costs by rate model">
      <OtsDataTable density={density} aria-label="Activity period costs by rate model">
        <OtsDataTableHead><OtsDataTableRow>
          <OtsDataTableHeaderCell>Rate model</OtsDataTableHeaderCell>
          <OtsDataTableHeaderCell>Company</OtsDataTableHeaderCell>
          <OtsDataTableHeaderCell align="right">Costed tickets</OtsDataTableHeaderCell>
          <OtsDataTableHeaderCell align="right">Cost</OtsDataTableHeaderCell>
        </OtsDataTableRow></OtsDataTableHead>
        <OtsDataTableBody>{summary.rateModels.map(rateModel => <OtsDataTableRow key={rateModel.rateModelId}>
          <OtsDataTableRowHeader>{rateModel.rateModelName || rateModel.rateModelId}</OtsDataTableRowHeader>
          <OtsDataTableCell>{rateModel.companyName || "—"}</OtsDataTableCell>
          <OtsDataTableCell align="right">{number.format(rateModel.costedTicketQty)}</OtsDataTableCell>
          <OtsDataTableCell align="right">{rateModel.totalCost == null ? "Masked" : currency.format(rateModel.totalCost)}</OtsDataTableCell>
        </OtsDataTableRow>)}</OtsDataTableBody>
      </OtsDataTable>
    </OtsDataTableSection>}

    {!!summary.personalResponsibilityCounts.length && <OtsDataTableSection title="My responsibilities">
      <OtsDataTable density={density} aria-label="Personal responsibility counts">
        <OtsDataTableHead><OtsDataTableRow>
          <OtsDataTableHeaderCell>Action</OtsDataTableHeaderCell>
          <OtsDataTableHeaderCell align="right">Open</OtsDataTableHeaderCell>
          <OtsDataTableHeaderCell align="right">In progress</OtsDataTableHeaderCell>
          <OtsDataTableHeaderCell align="right">Completed</OtsDataTableHeaderCell>
          <OtsDataTableHeaderCell align="right">Remaining</OtsDataTableHeaderCell>
        </OtsDataTableRow></OtsDataTableHead>
        <OtsDataTableBody>{summary.personalResponsibilityCounts.map(item => <OtsDataTableRow key={item.action}>
          <OtsDataTableRowHeader>{formatResponsibilityAction(item.action)}</OtsDataTableRowHeader>
          <OtsDataTableCell align="right">{number.format(item.openCount)}</OtsDataTableCell>
          <OtsDataTableCell align="right">{number.format(item.inProgressCount)}</OtsDataTableCell>
          <OtsDataTableCell align="right">{number.format(item.completedCount)}</OtsDataTableCell>
          <OtsDataTableCell align="right">{number.format(item.remainingCount)}</OtsDataTableCell>
        </OtsDataTableRow>)}</OtsDataTableBody>
      </OtsDataTable>
    </OtsDataTableSection>}
  </div>;
};

/** Activity Period profile with overview, reports, comments, stored/live rollups, responsibilities, and audit history. */
export const ActivityPeriodProfile = ({
  activityPeriod,
  displayName,
  summary,
  summaryLoading,
  summaryError,
  summarySource = "ROLLUP",
  changeLogs,
  changeLogsLoading,
  tenantCode,
  allowAddComment = false,
  showSummaryActions = false,
  refreshingSummary = false,
  refreshError,
  density = "default",
  brand = "water-go",
  colorScheme,
  onSummarySourceChange,
  onRefreshSummary,
  onClose,
}: ActivityPeriodProfileProps) => {
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  const reportCount = useMemo(() => collectActivityPeriodReports(activityPeriod).length, [activityPeriod]);
  const tabs = useMemo(() => [{
    id: "overview" as const,
    label: "Overview",
    content: <div className={density === "compact" ? "space-y-4" : "space-y-7"}>
      <DescriptionList className={density === "compact" ? "gap-y-4" : undefined}>
        <DescriptionItem label="Operator">{activityPeriod.operatorName}</DescriptionItem>
        <DescriptionItem label="Operator unit">{activityPeriod.operatorUnitName}</DescriptionItem>
        <DescriptionItem label="Hauler">{activityPeriod.truckCompanyName || "All haulers"}</DescriptionItem>
        <DescriptionItem label="Period"><OtsDateTime value={activityPeriod.fromDttm} displayTime={false} /> – <OtsDateTime value={activityPeriod.toDttm} displayTime={false} /></DescriptionItem>
        <DescriptionItem label="Tenant">{activityPeriod.database}</DescriptionItem>
        <DescriptionItem label="Reports">{number.format(activityPeriod.activityPeriodReportList?.length ?? 0)}</DescriptionItem>
        <DescriptionItem label="Documents">{number.format(activityPeriod.supplementals?.length ?? 0)}</DescriptionItem>
      </DescriptionList>
      <div className="border-t border-[var(--ots-outline-variant)] pt-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-[var(--ots-secondary)]">Summary</h2>
            <p className="mt-1 text-xs text-[var(--ots-text-muted)]">{summarySource === "LIVE" ? "Calculated live from current ticket data" : "Stored rollup for fast loading"}</p>
          </div>
          <StatusPill tone={summary?.totals.dirty ? "warning" : summarySource === "LIVE" ? "info" : "success"}>
            {summary?.totals.dirty ? "Rebuild pending" : summarySource === "LIVE" ? "Live" : "Stored"}
          </StatusPill>
        </div>
        {refreshError && <p role="alert" className="mb-4 rounded-md border border-[var(--ots-danger-outline)] bg-[var(--ots-danger-container)] px-3 py-2 text-sm text-[var(--ots-on-danger-container)]">The rollup could not be rebuilt. The current summary is unchanged.</p>}
        <SummaryContent summary={summary} loading={summaryLoading} error={summaryError} density={density}/>
      </div>
    </div>,
  }, {
    id: "reports" as const,
    label: "Reports",
    count: reportCount,
    content: <ActivityPeriodReports activityPeriod={activityPeriod} density={density}/>,
  }, {
    id: "comments" as const,
    label: "Comments",
    content: <EntityComments
      objectId={activityPeriod.id}
      objectType="ACTIVITYPERIOD"
      tenantCode={tenantCode ?? activityPeriod.database}
      density={density}
      allowAddComment={allowAddComment}
      commentReport={false}
      description="Operational notes recorded against this activity period."
      ariaLabel="Activity period messages and comments"/>,
  }, {
    id: "change-log" as const,
    label: "Change log",
    count: changeLogs?.flatMap(log => log.objectFieldChangeList ?? []).length,
    content: <EntityChangeLog logs={changeLogs} loading={changeLogsLoading} density={density} ariaLabel="Activity period change log"/>,
  }], [activityPeriod, allowAddComment, changeLogs, changeLogsLoading, density, refreshError, reportCount, summary, summaryError, summaryLoading, summarySource, tenantCode]);

  const refresh = async () => {
    try {
      await onRefreshSummary?.();
      setConfirmRefresh(false);
    } catch {
      // The data-connected page supplies refreshError; keep the confirmation open for retry/cancel.
    }
  };

  return <>
    <EntityProfile
      entityTypeLabel="Activity periods"
      title={displayName}
      subtitle={<span>{activityPeriod.operatorName}{activityPeriod.operatorUnitName ? ` · ${activityPeriod.operatorUnitName}` : ""}{activityPeriod.truckCompanyName ? ` · ${activityPeriod.truckCompanyName}` : ""}</span>}
      status={<ActivityPeriodHeaderBadges
        type={activityPeriod.type}
        status={activityPeriod.currentStatus}
        category={activityPeriod.category}
        density={density}/>}
      density={density}
      brand={brand}
      colorScheme={colorScheme}
      tabs={tabs}
      aside={<>
        <AdminPanel title="Status history" bodyClassName={density === "compact" ? "p-4" : "p-5"}>
          <OtsTimeline
            ariaLabel="Activity period status history"
            density={density}
            emptyState="No status history is available."
            items={[...(activityPeriod.activityPeriodStatusTimesList ?? [])]
              .sort((left, right) => right.lastModifiedDttm - left.lastModifiedDttm)
              .map((status, index) => ({
                id: status.id,
                label: formatResponsibilityAction(status.status),
                description: <span>{index === 0 ? "Current since" : "Entered"} <OtsDateTime value={status.lastModifiedDttm} /></span>,
                current: index === 0,
                tone: getActivityPeriodStatusTone(status.status),
              }))}/>
        </AdminPanel>
        <AdminPanel title="Record" bodyClassName={density === "compact" ? "p-4" : "p-5"}>
          <DescriptionList className="!grid-cols-1 gap-y-4">
            <DescriptionItem label="Created"><OtsDateTime value={activityPeriod.createDttm} /></DescriptionItem>
            <DescriptionItem label="Last modified"><OtsDateTime value={activityPeriod.lastModifiedDttm} /></DescriptionItem>
            <DescriptionItem label="Activity period ID"><span className="font-mono text-xs">{activityPeriod.id}</span></DescriptionItem>
          </DescriptionList>
        </AdminPanel>
      </>}
      actions={<>
        {showSummaryActions && <>
          <OtsActionButton size={density === "compact" ? "compact" : "default"} variant={summarySource === "ROLLUP" ? "primary" : "secondary"} icon={<CircleStackIcon/>} onClick={() => onSummarySourceChange?.("ROLLUP")}>Stored</OtsActionButton>
          <OtsActionButton size={density === "compact" ? "compact" : "default"} variant={summarySource === "LIVE" ? "primary" : "secondary"} onClick={() => onSummarySourceChange?.("LIVE")}>Live</OtsActionButton>
          <OtsAsyncActionButton size={density === "compact" ? "compact" : "default"} variant="secondary" icon={<ArrowPathIcon/>} isLoading={refreshingSummary} onClick={() => setConfirmRefresh(true)}>Rebuild</OtsAsyncActionButton>
        </>}
        {onClose && <OtsActionButton size={density === "compact" ? "compact" : "default"} variant="text" icon={<XMarkIcon/>} onClick={onClose}>Close</OtsActionButton>}
      </>}/>
    <ConfirmationDialog
      open={confirmRefresh}
      title="Rebuild this activity period rollup?"
      description="This recalculates and stores the summary from current ticket data. Live data is not changed."
      confirmLabel="Rebuild rollup"
      confirmingLabel="Rebuilding…"
      isConfirming={refreshingSummary}
      onConfirm={refresh}
      onCancel={() => setConfirmRefresh(false)}
      brand={brand}
      colorScheme={colorScheme}/>
  </>;
};
